import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Activity, Loader2, Database, Cloud, Clock, Calendar, TrendingDown, User, Play, ChevronDown, ChevronRight } from "lucide-react";

const RUNNABLE_JOBS = [
  ["decision_morning_refresh", "Refresh mattutino (date calde + prezzo struttura)", "Morning refresh (hot dates + property price)"],
  ["competitor_price_scan", "Scan competitor griglia 6 mesi (selezionati)", "6-month competitor grid scan (selected)"],
  ["market_nightly", "Scansione settimanale mercato (lunedì)", "Weekly market scan (Monday)"],
  ["market_monthly_full", "Scansione mensile completa (griglia 6 mesi)", "Full monthly scan (6-month grid)"],
  ["adaptive_competitor_refresh", "Re-check adattivo competitor", "Adaptive competitor re-check"],
  ["daily_refresh", "Refresh giornaliero struttura", "Daily property refresh"],
  ["nightly_events_refresh", "Aggiornamento eventi vicini", "Nearby events refresh"],
  ["search_trend_refresh", "Trend di ricerca (settimanale)", "Search trends (weekly)"],
  ["decision_snapshot_batch", "Batch snapshot decisioni (learning)", "Decision snapshot batch (learning)"],
  ["influence_recompute", "Ricalcolo influenza competitor", "Competitor influence recompute"],
];

export default function ScanStats({ lang }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const fmtDT = (s) => { try { return new Date(s).toLocaleString(lang === "en" ? "en-GB" : "it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [sched, setSched] = useState(null);
  const [runningId, setRunningId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/scan-stats?days=${days}`);
      setData(data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const loadSched = useCallback(async () => {
    try { const { data } = await api.get(`/admin/scheduler/status`); setSched(data); }
    catch { setSched(null); }
  }, []);
  useEffect(() => { loadSched(); }, [loadSched]);

  const [sessions, setSessions] = useState(null);
  const [sessDay, setSessDay] = useState(null);
  const [openSess, setOpenSess] = useState({});
  const loadSessions = useCallback(async (day) => {
    try {
      const q = day ? `?day=${day}` : "";
      const { data } = await api.get(`/admin/scan-sessions${q}`);
      setSessions(data); setSessDay(data.day);
    } catch { setSessions(null); }
  }, []);
  useEffect(() => { loadSessions(); }, [loadSessions]);
  const shiftDay = (delta) => {
    const base = sessDay ? new Date(sessDay + "T12:00:00") : new Date();
    base.setDate(base.getDate() + delta);
    loadSessions(base.toISOString().slice(0, 10));
  };

  const runManual = async (jobId, label) => {
    setRunningId(jobId);
    try {
      await api.post(`/admin/scheduler/run/${jobId}`);
      toast.success(L(`Avviata: ${label}`, `Started: ${label}`), {
        description: L("La scansione parte in background: i risultati compaiono tra qualche minuto.", "The scan runs in the background: results appear in a few minutes."),
      });
      setTimeout(() => { load(); loadSched(); }, 1500);
    } catch (e) {
      toast.error(L("Impossibile avviare la scansione", "Could not start the scan"),
        { description: e?.response?.data?.detail || "" });
    } finally {
      setRunningId(null);
    }
  };

  const Stat = ({ icon: Icon, label, value, color, testid }) => (
    <div className="bg-card border border-border rounded-lg p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-bold text-muted-foreground mb-1">
        <Icon className="w-4 h-4" style={{ color }} />{label}
      </div>
      <p className="text-2xl font-data font-bold" style={{ color }}>{value}</p>
    </div>
  );

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="scan-stats">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-semibold">{L("Stato scansioni mercato", "Market scan status")}</h2>
        </div>
        <div className="flex items-center gap-1.5 bg-secondary/60 rounded-lg p-1">
          {[7, 30, 90].map((d) => (
            <button key={d} data-testid={`scan-days-${d}`} onClick={() => setDays(d)}
              className={`px-3 py-1 rounded-md text-xs font-bold ${d === days ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}>
              {d}{L("g", "d")}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : !data ? (
        <p className="text-sm text-muted-foreground">{L("Nessun dato.", "No data.")}</p>
      ) : (
        <>
          {/* Global totals */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat icon={Activity} label={L("Scansioni totali", "Total scans")} value={data.totals.total} color="#6366f1" testid="scan-total" />
            <Stat icon={Cloud} label={L("Chiamate Apify", "Apify calls")} value={data.totals.apify} color="#ef4444" testid="scan-apify" />
            <Stat icon={Database} label={L("Servite da cache DB", "Served from DB cache")} value={data.totals.cache} color="#16a34a" testid="scan-cache" />
            <Stat icon={TrendingDown} label={L("Apify risparmiate", "Apify saved")} value={data.totals.cache_saving_pct != null ? `${data.totals.cache_saving_pct}%` : "—"} color="#0ea5e9" testid="scan-saving" />
          </div>

          {/* Scansioni del giorno: elenco chiamate Apify (riuscite + errori) con dettaglio */}
          <div className="bg-card border border-border rounded-lg p-5" data-testid="today-scan-sessions">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2"><Play className="w-5 h-5 text-primary" /><h3 className="font-semibold">{L("Chiamate Apify del giorno", "Apify calls of the day")}</h3></div>
              <div className="flex items-center gap-2 text-sm">
                <button data-testid="scan-day-prev" onClick={() => shiftDay(-1)} className="px-2 py-1 rounded border border-border hover:bg-muted">◀</button>
                <span className="font-medium" data-testid="scan-day-label">{sessDay || "—"}</span>
                <button data-testid="scan-day-next" onClick={() => shiftDay(1)} className="px-2 py-1 rounded border border-border hover:bg-muted">▶</button>
                <button data-testid="scan-day-today" onClick={() => loadSessions()} className="px-2 py-1 rounded border border-border hover:bg-muted text-xs">{L("Oggi", "Today")}</button>
              </div>
            </div>
            {sessions && (
              <div className="text-xs text-muted-foreground mb-3" data-testid="scan-day-totals">
                {L(`${sessions.total_calls || 0} chiamate Apify (${sessions.total_ok || 0} riuscite · ${sessions.total_errors || 0} errori) · costo stimato ~$${sessions.total_est_cost_usd} · ${sessions.total_sessions} scansioni · stima $${sessions.cost_per_run_usd}/chiamata riuscita`,
                   `${sessions.total_calls || 0} Apify calls (${sessions.total_ok || 0} ok · ${sessions.total_errors || 0} errors) · est. cost ~$${sessions.total_est_cost_usd} · ${sessions.total_sessions} scans · est. $${sessions.cost_per_run_usd}/successful call`)}
              </div>
            )}
            {!sessions || (sessions.sessions || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">{L("Nessuna chiamata in questa data.", "No calls on this date.")}</p>
            ) : (
              <div className="space-y-2">
                {sessions.sessions.map((s, i) => (
                  <div key={(s.session_id || "x") + i} data-testid={`scan-session-${i}`} className="border border-border/70 rounded-lg">
                    <button onClick={() => setOpenSess((o) => ({ ...o, [i]: !o[i] }))}
                      className="w-full flex flex-wrap items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/40">
                      {(s.calls || []).length > 0 ? (openSess[i] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />) : <span className="w-4" />}
                      <span className="font-medium">{s.label}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${s.trigger === "manual" ? "bg-amber-100 text-amber-800" : s.trigger === "scheduled" ? "bg-sky-100 text-sky-800" : s.trigger === "ondemand" ? "bg-indigo-100 text-indigo-800" : "bg-muted text-muted-foreground"}`}>
                        {s.trigger === "manual" ? L("Manuale", "Manual") : s.trigger === "scheduled" ? L("Automatica", "Scheduled") : s.trigger === "ondemand" ? L("On-demand (apertura pagina)", "On-demand (page open)") : L("Altro", "Other")}
                      </span>
                      {s.prop_name && <span className="text-muted-foreground">· {s.prop_name}</span>}
                      {s.started_at && <span className="text-muted-foreground">· {fmtDT(s.started_at)}</span>}
                      <span className="ml-auto text-right">
                        {s.read_from_db ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700"><Database className="w-3.5 h-3.5" />{L("letto da DB", "from DB")}</span>
                        ) : (
                          <span className="font-data">{s.apify_ok} {L("ok", "ok")}{s.errors ? <span className="text-red-600"> · {s.errors} err</span> : null} · ${s.est_cost_usd}</span>
                        )}
                      </span>
                    </button>
                    {openSess[i] && (s.calls || []).length > 0 && (
                      <div className="border-t border-border/60 divide-y divide-border/40" data-testid={`scan-session-calls-${i}`}>
                        {s.calls.map((c, j) => (
                          <div key={j} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                            <span className="text-muted-foreground w-14 shrink-0 font-data">{c.time}</span>
                            <span className="flex-1">{c.desc}</span>
                            <span className="text-muted-foreground">{c.items} {L("ris.", "res.")}</span>
                            <span className={c.ok ? "text-emerald-700" : "text-red-600"}>{c.ok ? "OK" : `ERR ${c.http_status || ""}`}</span>
                            <span className="font-data w-12 text-right">${c.est_cost_usd}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <p className="mt-1 text-[11px] text-muted-foreground">{L("Il costo è una STIMA per chiamata riuscita (compute + proxy); i fallimenti Apify non vengono fatturati. Il costo reale mensile è quello del tuo account Apify.", "Cost is an ESTIMATE per successful call (compute + proxy); Apify failures are not billed. The real monthly cost is the one on your Apify account.")}</p>
              </div>
            )}
          </div>

          {/* Schedule info */}
          <div className="bg-card border border-border rounded-lg p-5" data-testid="scan-schedule">
            <div className="flex items-center gap-2 mb-3"><Clock className="w-5 h-5 text-primary" /><h3 className="font-semibold">{L("Scansioni automatiche", "Automatic scans")}</h3></div>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2"><Calendar className="w-4 h-4 mt-0.5 text-primary shrink-0" /><span><b>{L("Settimanale", "Weekly")}:</b> {data.schedule.weekly_grid}</span></li>
              {data.schedule.competitor_daily && (
                <li className="flex items-start gap-2"><Activity className="w-4 h-4 mt-0.5 text-primary shrink-0" /><span><b>{L("Competitor selezionati", "Selected competitors")}:</b> {data.schedule.competitor_daily}</span></li>
              )}
              {data.schedule.decision_morning && (
                <li className="flex items-start gap-2"><Clock className="w-4 h-4 mt-0.5 text-primary shrink-0" /><span><b>{L("Mattutino", "Morning")}:</b> {data.schedule.decision_morning}</span></li>
              )}
              <li className="flex items-start gap-2"><Calendar className="w-4 h-4 mt-0.5 text-primary shrink-0" /><span><b>{L("Mensile", "Monthly")}:</b> {data.schedule.monthly_full}</span></li>
              <li className="flex items-start gap-2"><Activity className="w-4 h-4 mt-0.5 text-primary shrink-0" /><span><b>{L("Adattiva", "Adaptive")}:</b> {data.schedule.adaptive_recheck}</span></li>
              <li className="flex items-start gap-2"><Database className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" /><span><b>{L("Cache", "Cache")}:</b> {L(`I dati restano validi ${data.schedule.cache_ttl_hours}h e sono condivisi tra tutte le sezioni: entro ${data.schedule.cache_ttl_hours} ore si pesca dal database, senza chiamare Apify.`, `Data stays valid for ${data.schedule.cache_ttl_hours}h and is shared across all sections: within ${data.schedule.cache_ttl_hours} hours it is served from the database, no Apify call.`)}</span></li>
            </ul>
            {(data.last_runs || data.next_runs) && (
              <div className="mt-4 pt-3 border-t border-border grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs" data-testid="scan-last-runs">
                {[
                  ["decision_morning_refresh", L("Refresh mattutino (date calde + prezzo struttura)", "Morning refresh (hot dates + property price)")],
                  ["competitor_price_scan", L("Scan competitor 6 mesi (selezionati · giornaliero, salta se freschi)", "6-month competitor scan (selected · daily, skips if fresh)")],
                ].map(([id, label]) => {
                  const lr = (data.last_runs || {})[id];
                  const nr = (data.next_runs || {})[id];
                  const fmt = (s) => { try { return new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return s; } };
                  const ds = lr?.detail?.date_scans;
                  const dsNote = ds != null ? (ds === 0 ? ` · ${L("0 date (dati già freschi — nessuna chiamata Apify)", "0 dates (data already fresh — no Apify call)")}` : ` · ${ds} ${L("date scansionate", "dates scanned")}`) : "";
                  return (
                    <div key={id} className="flex flex-col" data-testid={`run-${id}`}>
                      <span className="font-semibold">{label}</span>
                      <span className="text-muted-foreground">{L("Ultima", "Last")}: {lr?.last_run_at ? fmt(lr.last_run_at) : L("mai (in attesa)", "never (pending)")}{lr?.detail?.self_dates != null ? ` · ${lr.detail.self_dates} ${L("prezzi struttura", "property prices")}` : ""}{lr?.detail?.competitor_dates != null ? ` · ${lr.detail.competitor_dates} ${L("date competitor", "competitor dates")}` : ""}{dsNote}</span>
                      <span className="text-muted-foreground">{L("Prossima", "Next")}: {nr ? fmt(nr) : "—"}</span>
                    </div>
                  );
                })}
                {data.scheduler_running === false && (
                  <p className="sm:col-span-2 text-amber-600 dark:text-amber-400">{L("Attenzione: lo scheduler non risulta attivo.", "Warning: the scheduler is not running.")}</p>
                )}
              </div>
            )}
          </div>

          {/* Manual run — ADMIN ONLY */}
          <div className="bg-card border border-border rounded-lg p-5" data-testid="scan-manual-run">
            <div className="flex items-center gap-2 mb-1"><Play className="w-5 h-5 text-primary" /><h3 className="font-semibold">{L("Esegui scansioni manualmente", "Run scans manually")}</h3></div>
            <p className="text-xs text-muted-foreground mb-4">{L("Solo admin. Utile quando le finestre pianificate (es. lunedì/mensile) non ricorrono a breve o dopo il reset del credito Apify: avvia subito il tipo di scansione che vuoi.", "Admin only. Useful when scheduled windows (e.g. Monday/monthly) won't recur soon or after the Apify credit resets: trigger the scan type you need right now.")}</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {RUNNABLE_JOBS.map(([id, it, en]) => {
                const job = (sched?.jobs || []).find((j) => j.id === id);
                const fmt = (s) => { try { return new Date(s).toLocaleString(lang === "en" ? "en-GB" : "it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return s; } };
                return (
                  <div key={id} data-testid={`sched-job-${id}`} className="flex items-center justify-between gap-3 border border-border rounded-lg px-3 py-2.5">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{L(it, en)}</div>
                      <div className="text-[11px] text-muted-foreground">{L("Prossima", "Next")}: {job?.next_run_time ? fmt(job.next_run_time) : "—"}</div>
                    </div>
                    <button
                      data-testid={`run-job-${id}`}
                      disabled={runningId === id}
                      onClick={() => runManual(id, L(it, en))}
                      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 transition-opacity">
                      {runningId === id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                      {L("Esegui", "Run")}
                    </button>
                  </div>
                );
              })}
            </div>
            {sched?.scheduler_running === false && (
              <p className="mt-3 text-xs text-amber-600 dark:text-amber-400">{L("Lo scheduler non risulta attivo: i job non partiranno.", "The scheduler is not running: jobs won't fire.")}</p>
            )}
          </div>

          {/* Per-user table */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-border"><h3 className="font-semibold text-sm">{L("Scansioni per utente", "Scans per user")}</h3></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="scan-user-table">
                <thead className="bg-secondary/60 text-[11px] uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-2">{L("Utente", "User")}</th>
                    <th className="text-right px-4 py-2">{L("Totali", "Total")}</th>
                    <th className="text-right px-4 py-2 text-red-500">Apify</th>
                    <th className="text-right px-4 py-2 text-emerald-600">{L("Cache DB", "DB cache")}</th>
                    <th className="text-right px-4 py-2">{L("Risparmio", "Saving")}</th>
                    <th className="text-left px-4 py-2">{L("Ultima scansione", "Last scan")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.users.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">{L("Nessuna scansione manuale nel periodo.", "No manual scans in this period.")}</td></tr>
                  ) : data.users.map((u, i) => (
                    <tr key={u.user_id} className="border-t border-border hover:bg-accent/20" data-testid={`scan-user-${i}`}>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <div className="font-medium">{u.name}{u.role === "admin" && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-primary text-primary-foreground uppercase">admin</span>}</div>
                            {u.email && <div className="text-[11px] text-muted-foreground">{u.email}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right font-data font-semibold">{u.total}</td>
                      <td className="px-4 py-2 text-right font-data text-red-500">{u.apify}</td>
                      <td className="px-4 py-2 text-right font-data text-emerald-600">{u.cache}</td>
                      <td className="px-4 py-2 text-right font-data">{u.cache_saving_pct != null ? `${u.cache_saving_pct}%` : "—"}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">{u.last_scan ? new Date(u.last_scan).toLocaleString(lang === "en" ? "en-GB" : "it-IT") : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </motion.div>
  );
}
